import { Component, Input, OnInit, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatTimepickerOption } from "@angular/material/timepicker";
import { IonModal, ModalController } from "@ionic/angular";
import { ToastType } from "../../enums/global-enums";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { DetailAktDataRetrieveResponse } from "../../../api/models/detail-akt-data-retrieve-response";
import { KontaktDetail } from "../../../api/models/kontakt-detail";
import { KontaktDetailUpdate } from "../../../api/models/kontakt-detail-update";
import { DetailService } from "../../../api/services/detail.service";
import { KontaktService } from "../../../api/services/kontakt.service";
import { TermineService } from "../../../api/services/termine.service";
import {
  KundenterminStatus,
  TerminBerichtUrgentStatus,
  TerminRueckfrageAnStatus,
  TerminWorkflowProcess,
} from "../../enums/global-enums";
import { isKontaktAllowedForEdit, removeCustomError } from "../../global-functions/global-functions";
import { EmailService } from "../../services/email.service";
import { LoveItAlertService } from "../../services/love-it-alert.service";
import { LoveItToastService } from "../../services/love-it-toast.service";
import { TerminService } from "../../services/termin.service";
import { ComposeEmailComponent, MailData } from "../compose-email/compose-email.component";
import { KontaktdatenAnsichtModalComponent } from "../kontaktdaten-ansicht-modal/kontaktdaten-ansicht-modal.component";
import { RueckfrageAnEmailComponent } from "../rueckfrage-an-email/rueckfrage-an-email.component";

@Component({
  selector: "app-termin-bearbeiten",
  templateUrl: "./termin-bearbeiten.component.html",
  styleUrls: ["./termin-bearbeiten.component.scss"],
  standalone: false,
})
export class TerminBearbeitenComponent implements OnInit {
  @Input() aktId: number;
  @Input() aktTitle: string;
  @Input() aktType: string;
  @Input() aktStatus: string;
  @Input() isTechnik: boolean;
  @Input() berichtUrgenzTerminId: number;
  @Input() terminWorkflowProcess: TerminWorkflowProcess;
  @Input() terminId: number;
  @Input() terminDatum: Date;

  @ViewChild("emailPruefenModal") emailPruefenModal: IonModal;
  @ViewChild("grundAngebenModal") grundAngebenModal: IonModal;

  terminBearbeitenFormGroup: UntypedFormGroup;
  emailPruefenFormGroup: UntypedFormGroup;
  grundAngebenFormGroup: UntypedFormGroup;
  submitted: boolean = false;
  submittedEmailPruefenModal: boolean = false;
  submittedGrundAngebenModal: boolean = false;
  emailPruefenModalIsSaving: boolean = false;
  fetchingKundentermin: boolean = false;
  kundenterminId: number = null;
  kundentermin: any;
  details: DetailAktDataRetrieveResponse = null;
  berichtUrExists: boolean = null;

  datumUhrzeitVisibleTerminStatus: KundenterminStatus[] = [KundenterminStatus.Vereinbart];

  terminTitle: string = "";

  currentTerminStatusOptionsTitle: string = "";
  currentTerminStatusOptions: {
    label: string;
    value: KundenterminStatus | TerminRueckfrageAnStatus | TerminBerichtUrgentStatus;
  }[] = [];

  kundenterminStatusOptionsTechnik: {
    label: string;
    value: KundenterminStatus;
  }[] = this.terminService.buildTerminOptions({
    enumObj: KundenterminStatus,
    labels: this.terminService.kundenterminLabels,
    includeValues: this.terminService.TECHNIK_VALUES,
  });

  kundenterminStatusOptionsMsr: {
    label: string;
    value: KundenterminStatus;
  }[] = this.terminService.buildTerminOptions({
    enumObj: KundenterminStatus,
    labels: this.terminService.kundenterminLabels,
    includeValues: this.terminService.MSR_VALUES,
  });

  rueckfrageAnStatusOptions: {
    label: string;
    value: TerminRueckfrageAnStatus;
  }[] = this.terminService.buildTerminOptions({
    enumObj: TerminRueckfrageAnStatus,
    labels: this.terminService.rueckfrageAnLabels,
  });

  rueckfrageAnGrundOptions: {
    label: string;
    value: string;
  }[] = [
    {
      label: "Recherche dauert länger als 7 Tage",
      value: "Recherche dauert länger als 7 Tage",
    },
    {
      label: "Verrechnung, da neuer Sachverhalt / Schadenerweiterung",
      value: "Verrechnung, neuer Sachverhalt/Schadenerweiterung",
    },
    {
      label: "Verrechnung, da mehrere Rechnungen zu prüfen sind",
      value: "Verrechnung, da mehrere Rechnungen zu prüfen sind",
    },
    {
      label: "Verrechnung, da x-te berechtigte Rückfrage",
      value: "Verrechnung, da x-te berechtigte Rückfrage",
    },
  ];

  berichtUrStatusOptions: {
    label: string;
    value: TerminBerichtUrgentStatus;
  }[] = this.terminService.buildTerminOptions({
    enumObj: TerminBerichtUrgentStatus,
    labels: this.terminService.berichtUrLabels,
  });

  customTimeOptions: MatTimepickerOption<Date>[] = this.timeOptions;

  get terminDateValue() {
    return this.terminBearbeitenFormGroup.get("DatumNeu")?.value;
  }

  get terminTimeValue() {
    return this.formatTime(this.terminBearbeitenFormGroup.get("UhrzeitNeu")?.value);
  }

  get isKundentermin() {
    return this.terminWorkflowProcess === TerminWorkflowProcess.Kundentermin;
  }

  get isRueckfrageAn() {
    return this.terminWorkflowProcess === TerminWorkflowProcess.RueckfrageAn;
  }

  get isBerichtUrgent() {
    return this.terminWorkflowProcess === TerminWorkflowProcess.BerichtUrgent;
  }

  constructor(
    private modalController: ModalController,
    private termineServiceBackend: TermineService,
    private fb: FormBuilder,
    private oidcSecurityService: OidcSecurityService,
    private loveItToastService: LoveItToastService,
    private detailService: DetailService,
    private loveItAlertService: LoveItAlertService,
    private kontaktService: KontaktService,
    private terminService: TerminService,
    private emailService: EmailService,
  ) {}

  ngOnInit() {
    const now = new Date();
    const currentDate = now.toISOString().split("T")[0];

    this.terminBearbeitenFormGroup = this.fb.group({
      BemerkungNeu: [null, [this.bemerkungRequiredValidator(), this.bemerkungContentValidator()]],
      BetreffNeu: [null],
      Rueckstellung: [null, this.rueckstellungValidator()],
      DatumNeu: [currentDate, this.dateValidator()],
      UhrzeitNeu: [now, [this.dateValidator(), this.timeValidator()]],
      KontaktDatenRecherche: [false, Validators.required],
      TerminStatus: [null, Validators.required],
    });

    switch (this.terminWorkflowProcess) {
      case TerminWorkflowProcess.Kundentermin:
        this.terminBearbeitenFormGroup.get("TerminStatus").setValue(KundenterminStatus.Vereinbart);
        this.terminTitle = "Termin bestätigen";
        this.currentTerminStatusOptionsTitle = "Status des Kundentermins";

        if (this.isTechnik) {
          this.currentTerminStatusOptions = this.kundenterminStatusOptionsTechnik;
        } else {
          this.currentTerminStatusOptions = this.kundenterminStatusOptionsMsr;
        }
        if (this.terminId) {
          this.kundenterminId = this.terminId;
        } else {
          this.fetchKundentermin();
        }
        if (this.berichtUrgenzTerminId) {
          this.berichtUrExists = true;
        } else {
          this.fetchBerichtur();
        }

        break;
      case TerminWorkflowProcess.RueckfrageAn:
        this.terminBearbeitenFormGroup.get("TerminStatus").setValue(TerminRueckfrageAnStatus.Abgeschlossen);
        this.terminTitle = "Rückfrage beantworten zu";
        this.terminBearbeitenFormGroup.get("BetreffNeu").setValue("Rückfragen an Auftragnehmer");
        this.terminBearbeitenFormGroup.get("BetreffNeu").disable();
        this.currentTerminStatusOptionsTitle = "Status der Rückfrage";
        this.currentTerminStatusOptions = this.rueckfrageAnStatusOptions;
        break;
      case TerminWorkflowProcess.BerichtUrgent:
        this.terminBearbeitenFormGroup.get("TerminStatus").setValue(TerminBerichtUrgentStatus.RueckstellungKurzinfo);
        this.terminTitle = "Bericht ur beantworten";
        this.terminBearbeitenFormGroup.get("BetreffNeu").setValue("Urgenz des Berichtes beim AN");
        this.terminBearbeitenFormGroup.get("BetreffNeu").disable();
        this.currentTerminStatusOptionsTitle = "Statusinfo zur Berichtserstellung";
        this.currentTerminStatusOptions = this.berichtUrStatusOptions;
        this.terminBearbeitenFormGroup.get("TerminStatus").valueChanges.subscribe(() => {
          this.terminBearbeitenFormGroup.get("Rueckstellung").updateValueAndValidity();
        });
        break;
    }

    this.emailPruefenFormGroup = this.fb.group({
      email: [null, [this.emailValidator()]],
    });
    this.grundAngebenFormGroup = this.fb.group({
      grund: [null, Validators.required],
    });

    this.terminBearbeitenFormGroup.get("TerminStatus")?.valueChanges.subscribe((status) => {
      if (this.datumUhrzeitVisibleTerminStatus.includes(status)) {
        if (!this.terminBearbeitenFormGroup.get("DatumNeu").dirty) {
          this.terminBearbeitenFormGroup.get("DatumNeu")?.setValue(currentDate);
        }

        if (!this.terminBearbeitenFormGroup.get("UhrzeitNeu").dirty) {
          this.terminBearbeitenFormGroup.get("UhrzeitNeu")?.setValue(now);
        }
      }

      this.terminBearbeitenFormGroup.get("BemerkungNeu")?.updateValueAndValidity();
      this.terminBearbeitenFormGroup.get("DatumNeu")?.updateValueAndValidity();
      this.terminBearbeitenFormGroup.get("UhrzeitNeu")?.updateValueAndValidity();
    });

    this.fetchDetails();
  }

  cancelButton() {
    if (this.terminBearbeitenFormGroup.dirty) {
      this.abbrechenBestaetigenDialogAnzeigen();
    } else {
      this.modalController.dismiss(null, "cancel");
    }
  }

  cancelButtonEmailPruefenModal() {
    this.emailPruefenModal.dismiss(null, "cancel");
  }

  cancelButtonGrundAngebenModal() {
    this.grundAngebenModal.dismiss(null, "cancel");
  }

  async submit() {
    this.submitted = true;

    if (!this.datumUhrzeitVisibleTerminStatus.includes(this.terminBearbeitenFormGroup.get("TerminStatus").value)) {
      this.terminBearbeitenFormGroup.get("DatumNeu").setValue(new Date().toISOString().split("T")[0]);
      this.terminBearbeitenFormGroup.get("UhrzeitNeu").setValue(new Date());
    }

    this.terminBearbeitenFormGroup.updateValueAndValidity();
    if (this.terminBearbeitenFormGroup.invalid) {
      this.terminBearbeitenFormGroup.markAllAsTouched();
      return;
    }

    if (!this.details) {
      this.loveItToastService.showToastMessage("Fehler beim Laden des Aktes. Speichern fehlgeschlagen.", ToastType.Error);
      this.fetchDetails();
      return;
    }

    switch (this.terminWorkflowProcess) {
      case TerminWorkflowProcess.Kundentermin:
        if (!this.kundenterminId) {
          this.loveItToastService.showToastMessage("Fehler beim Laden des Kundentermins. Speichern fehlgeschlagen.", ToastType.Error);
          this.fetchKundentermin();
          return;
        }
        if (isKontaktAllowedForEdit(this.details.Contacts.Ansprechpartner)) {
          await this.emailPruefenModal.present();
        } else {
          await this.openKundenterminEmailVersendenModal();
        }
        break;
      case TerminWorkflowProcess.RueckfrageAn:
        const rueckfrageAnStatus: TerminRueckfrageAnStatus = this.terminBearbeitenFormGroup?.get("TerminStatus")?.value;
        if (rueckfrageAnStatus === TerminRueckfrageAnStatus.UeberBOVersendet) {
          await this.grundAngebenModal.present();
        } else {
          await this.openRueckfrageAnEmailVersendenModal();
        }
        break;
      case TerminWorkflowProcess.BerichtUrgent:
        await this.openBerichtUrEmailVersendenModal();
        break;
    }
  }

  async submitEmailPruefenModal() {
    this.submittedEmailPruefenModal = true;
    this.emailPruefenFormGroup.updateValueAndValidity();
    if (this.emailPruefenFormGroup.invalid) {
      this.emailPruefenFormGroup.markAllAsTouched();
      return;
    }

    if (this.emailPruefenFormGroup.dirty) {
      this.emailPruefenModalIsSaving = true;
      let ansprechpartnerContactDetail: KontaktDetailUpdate = this.details.Contacts.Ansprechpartner;
      ansprechpartnerContactDetail.Email = this.emailPruefenFormGroup.get("email").value;
      ansprechpartnerContactDetail.token = this.oidcSecurityService.getToken();
      ansprechpartnerContactDetail.akt_id = this.aktId.toString();
      this.kontaktService.kontaktSaveCreate(ansprechpartnerContactDetail).subscribe({
        next: async () => {
          this.detailService
            .detailRetrieveCreate({
              akt_id: this.aktId,
              token: this.oidcSecurityService.getToken(),
            })
            .subscribe(async (response: any) => {
              this.details = response;
              this.emailPruefenModalIsSaving = false;
              await this.openKundenterminEmailVersendenModal();
            });
        },
        error: async () => {
          this.emailPruefenModalIsSaving = false;
          this.loveItToastService.showToastMessage("Kontakt konnte nicht gespeichert werden.", ToastType.Error);
        },
      });
    } else {
      await this.openKundenterminEmailVersendenModal();
    }
  }

  async submitGrundAngebenModal() {
    this.submittedGrundAngebenModal = true;
    this.grundAngebenFormGroup.updateValueAndValidity();
    if (this.grundAngebenFormGroup.invalid) {
      this.grundAngebenFormGroup.markAllAsTouched();
      return;
    }
    await this.openRueckfrageAnEmailVersendenModal(this.grundAngebenFormGroup.get("grund").value);
  }

  fetchKundentermin() {
    this.fetchingKundentermin = true;
    this.terminBearbeitenFormGroup.get("BetreffNeu").disable();
    this.termineServiceBackend
      .termineOdataAktKundenterminCreate({
        token: this.oidcSecurityService.getToken(),
        akt_id: this.aktId,
      })
      .subscribe((response: any) => {
        if (response.length > 1) {
          this.kundentermin = response.find((termin) => !termin.OK);
        } else {
          this.kundentermin = response[0];
        }
        this.kundenterminId = this.kundentermin?.TerminID;
        this.terminBearbeitenFormGroup.get("BetreffNeu").setValue(this.kundentermin?.Betreff);
        this.terminBearbeitenFormGroup.get("BetreffNeu").enable();
        this.fetchingKundentermin = false;
      });
  }

  fetchDetails() {
    this.detailService
      .detailRetrieveCreate({
        akt_id: this.aktId,
        token: this.oidcSecurityService.getToken(),
      })
      .subscribe((response: any) => {
        this.details = response;
        this.emailPruefenFormGroup.get("email").setValue(this.details.Contacts?.Ansprechpartner?.Email);
      });
  }

  fetchBerichtur() {
    this.termineServiceBackend
      .termineOdataAktBerichturCreate({
        token: this.oidcSecurityService.getToken(),
        akt_id: this.aktId,
      })
      .subscribe((response: any) => {
        this.berichtUrExists = response.length > 0;
      });
  }

  dateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const terminStatus: KundenterminStatus = this.terminBearbeitenFormGroup?.get("TerminStatus")?.value;

      if (terminStatus == KundenterminStatus.Vereinbart && !control.value) {
        return { required: true };
      }

      if (!control.value) {
        return null;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const fiveDaysAgo = new Date();
      fiveDaysAgo.setDate(today.getDate() - 5);
      fiveDaysAgo.setHours(0, 0, 0, 0);

      const maxDate = new Date(today);
      maxDate.setDate(today.getDate() + 42);

      const inputDate = new Date(control.value);
      inputDate.setHours(0, 0, 0, 0);

      if (terminStatus === KundenterminStatus.Vereinbart) {
        if (inputDate < fiveDaysAgo)
          return {
            customDateError: "Datum darf nicht mehr als fünf Tage in der Vergangenheit liegen!",
          };
      } else {
        if (inputDate < today) {
          return {
            customDateError: "Datum darf nicht in der Vergangenheit liegen!",
          };
        }
      }

      if (inputDate > maxDate) {
        return {
          customDateError: "Datum darf nicht mehr als 6 Wochen in der Zukunft liegen!",
        };
      }

      return null;
    };
  }

  timeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const inputTime = new Date(control.value);

      if (inputTime.getHours() === 0 && inputTime.getMinutes() === 0) {
        return {
          customTimeError: "Uhrzeit mit 00:00 ist ungültig!",
        };
      }

      return null;
    };
  }

  bemerkungRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.terminBearbeitenFormGroup) {
        return null;
      }
      const requiredForKundenterminStatus: KundenterminStatus[] = [KundenterminStatus.SpaeterMoeglich];
      const requiredForTerminRueckfrageAnStatus: TerminRueckfrageAnStatus[] = [
        TerminRueckfrageAnStatus.UeberBOVersendet,
      ];

      const status = this.terminBearbeitenFormGroup.get("TerminStatus")?.value;
      const isRequired =
        requiredForKundenterminStatus.includes(status) || requiredForTerminRueckfrageAnStatus.includes(status);

      if (isRequired && !control.value) {
        return { required: true };
      }

      return null;
    };
  }

  bemerkungContentValidator(): ValidationErrors {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!this.terminBearbeitenFormGroup) {
        return null;
      }
      const control = this.terminBearbeitenFormGroup.get("BemerkungNeu");

      const isContentValid = () => {
        if (!control.value) {
          return true;
        }
        const valueWithoutWhitespace = control.value.replace(/\s+/g, "");
        return valueWithoutWhitespace.length > 4;
      };
      if (!isContentValid()) {
        return {
          customError: "Hinweis von / an BO muss mindestens 5 Zeichen lang sein!",
        };
      }

      removeCustomError(control);
      return null;
    };
  }

  rueckstellungValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.terminBearbeitenFormGroup) {
        return null;
      }

      const requiredForTerminBerichtUrStatus: TerminBerichtUrgentStatus[] = [
        TerminBerichtUrgentStatus.RueckstellungKurzinfo,
        TerminBerichtUrgentStatus.Verzoegerungsinfo,
      ];

      const status = this.terminBearbeitenFormGroup.get("TerminStatus")?.value;
      const isRequired = requiredForTerminBerichtUrStatus.includes(status);

      if (isRequired && !control.value) {
        return { required: true };
      }

      return null;
    };
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value) {
        const emailRegex = this.emailService.getEmailValidationRegex();
        const isValidEmail = emailRegex.test(control.value);
        if (!isValidEmail) {
          return { customError: "Ungültige E-Mail Adresse!" };
        }
      }
      return null;
    };
  }

  isControlRequired(controlName: string): boolean {
    const control = this.terminBearbeitenFormGroup.get(controlName);
    if (!control || !control.validator) {
      return false;
    }
    const validator = control.validator({} as any);
    return validator && validator["required"];
  }

  async openKontaktdatenAnsichtModal(person: KontaktDetail) {
    const modal = await this.modalController.create({
      component: KontaktdatenAnsichtModalComponent,
      cssClass: [],
      componentProps: {
        titel: "",
        enableEdit: false,
        anspruchsteller: person.Name,
        auftrag: this.details.Title,
        schadensnummer: this.details.Incident_number,
        fca: this.details.Contacts?.FCA,
        instance: person,
        akt_id: this.aktId,
      },
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();
  }

  async openKundenterminEmailVersendenModal() {
    const requiredDefaultToBackofficeStatus: KundenterminStatus[] = [
      KundenterminStatus.NichtMoeglichPostweg,
      KundenterminStatus.SpaeterMoeglich,
    ];

    const terminDateTime = new Date(`${this.terminDateValue}T${this.terminTimeValue}:00`);

    const modal = await this.modalController.create({
      component: ComposeEmailComponent,
      cssClass: ["bigger-modal"],
      backdropDismiss: false,
      componentProps: {
        aktId: this.aktId,
        aktTitle: this.aktTitle,
        defaultSubject: this.emailService.getDefaultEmailSubject(this.details, terminDateTime),
        setRecipientToBackoffice: requiredDefaultToBackofficeStatus.includes(
          this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
        ),
        terminBestaetigenFormGroup: this.terminBearbeitenFormGroup,
        terminStatus: this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
        date: this.terminDateValue,
        formattedTime: this.terminTimeValue,
        terminId: this.kundenterminId,
        details: this.details,
        submitMessage: "Der Termin wird bestätigt und die E-Mail wird versendet.",
        callAfterSubmit: (mail: MailData) => {
          const dateValue = this.terminBearbeitenFormGroup?.get("DatumNeu")?.value;
          const timeValue = this.formatTime(this.terminBearbeitenFormGroup?.get("UhrzeitNeu")?.value);
          const dateParameter = dateValue && timeValue ? `${dateValue}T${timeValue}` : null;

          return new Promise<void>((resolve, reject) => {
            this.termineServiceBackend
              .termineOdataKundenterminBestaetigenCreate({
                akt_id: this.aktId,
                token: this.oidcSecurityService.getToken(),
                BemerkungNeu: this.terminBearbeitenFormGroup?.get("BemerkungNeu")?.value,
                BetreffNeu: this.terminBearbeitenFormGroup?.get("BetreffNeu")?.value,
                DatumNeu: dateParameter,
                KontaktDatenRecherche: this.terminBearbeitenFormGroup.get("KontaktDatenRecherche").value,
                KundenterminStatus: this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
                TerminId: this.kundenterminId,
                to: mail.to,
                cc: mail.cc,
                bcc: mail.bcc,
                subject: mail.subject,
                message: mail.message,
                attachments: mail.attachments,
                date: mail.date,
              })
              .subscribe({
                next: (result) => {
                  resolve();
                },
                error: async (error) => {
                  const message = `Fehler beim Bestätigen des Kundentermins!`;
                  reject(new Error(message));
                },
              });
          });
        },
      },
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role == "confirm") {
      try {
        let topModal = await this.modalController.getTop();
        while (topModal) {
          await this.modalController.dismiss(null, "confirm");
          topModal = await this.modalController.getTop();
        }
      } catch (error) {
        console.error("Error dismissing modals:", error);
      }
    }
  }

  async openRueckfrageAnEmailVersendenModal(grund: string = null) {
    const modal = await this.modalController.create({
      component: RueckfrageAnEmailComponent,
      cssClass: ["bigger-modal"],
      backdropDismiss: false,
      componentProps: {
        aktId: this.aktId,
        aktTitle: this.aktTitle,
        terminBearbeitenFormGroup: this.terminBearbeitenFormGroup,
        terminId: this.terminId,
        details: this.details,
        grund: grund,
      },
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role == "confirm") {
      try {
        let topModal = await this.modalController.getTop();
        while (topModal) {
          await this.modalController.dismiss(null, "confirm");
          topModal = await this.modalController.getTop();
        }
      } catch (error) {
        console.error("Error dismissing modals:", error);
      }
    }
  }

  async openBerichtUrEmailVersendenModal() {
    const terminDateTime = new Date(`${this.terminDateValue}T${this.terminTimeValue}:00`);

    const modal = await this.modalController.create({
      component: ComposeEmailComponent,
      cssClass: ["bigger-modal"],
      backdropDismiss: false,
      componentProps: {
        aktId: this.aktId,
        aktTitle: this.aktTitle,
        defaultSubject: this.emailService.getDefaultEmailSubject(this.details, terminDateTime),
        terminBestaetigenFormGroup: this.terminBearbeitenFormGroup,
        terminStatus: this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
        rueckstellung: this.terminBearbeitenFormGroup?.get("Rueckstellung")?.value,
        date: this.terminDateValue,
        formattedTime: this.terminTimeValue,
        terminId: this.kundenterminId,
        details: this.details,
        includeVUSBRecipient: true,
        setRecipientToBackoffice: false,
        submitMessage: "Die Bericht Urgenz wird bearbeitet und die E-Mail wird versendet.",
        callAfterSubmit: (mail: MailData) => {
          return new Promise<void>((resolve, reject) => {
            this.termineServiceBackend
              .termineOdataBerichturBearbeitenCreate({
                akt_id: this.aktId,
                token: this.oidcSecurityService.getToken(),
                RueckstellungBetrag: this.terminBearbeitenFormGroup?.get("Rueckstellung")?.value ?? null,
                BerichtUrStatus: this.terminBearbeitenFormGroup?.get("TerminStatus")?.value,
                TerminId: this.terminId,
                to: mail.to,
                cc: mail.cc,
                bcc: mail.bcc,
                subject: mail.subject,
                message: mail.message,
                attachments: mail.attachments,
                date: mail.date,
              })
              .subscribe({
                next: (result) => {
                  resolve();
                },
                error: async (error) => {
                  const message = `Fehler beim bearbeiten der Bericht Urgenz!`;
                  reject(new Error(message));
                },
              });
          });
        },
      },
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role == "confirm") {
      try {
        let topModal = await this.modalController.getTop();
        while (topModal) {
          await this.modalController.dismiss(null, "confirm");
          topModal = await this.modalController.getTop();
        }
      } catch (error) {
        console.error("Error dismissing modals:", error);
      }
    }
  }

  async abbrechenBestaetigenDialogAnzeigen() {
    const alert = await this.loveItAlertService.create({
      header: "Ungespeicherte Änderungen vorhanden",
      message: "Wollen Sie alle Änderungen am Termin verwerfen und das Fenster schließen?",
      buttons: [
        {
          cssClass: "color-primary",
          text: "Abbrechen",
          role: "cancel",
        },
        {
          cssClass: "color-danger",
          text: "Verwerfen",
          handler: () => {
            this.modalController.dismiss(null, "cancel");
          },
        },
      ],
    });
    await alert.present();
  }

  get timeOptions() {
    const startTime = 5;
    const endTime = 22;
    const interval = 15;
    const options = [];

    for (let hour = startTime; hour <= endTime; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        if (hour === endTime && minute > 0) {
          break;
        }
        options.push({
          label: `${hour}:${minute < 10 ? "0" + minute : minute}`,
          value: new Date(2024, 0, 1, hour, minute),
        });
      }
    }
    return options;
  }

  formatTime(date: Date): string {
    return new Intl.DateTimeFormat("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(date);
  }
}
